"use client";

import { cn } from "@/utils/cn";
import { Check, ChevronDown } from "@tailgrids/icons";
import type { ComponentProps } from "react";
import React, { createContext, useContext } from "react";
import {
  type ButtonProps as AriaButtonProps,
  Select as AriaSelect,
  type SelectProps as AriaSelectProps,
  Button,
  DialogTrigger,
  FieldError,
  Header,
  type Key,
  Label,
  type LabelProps,
  ListBox,
  ListBoxItem,
  type ListBoxItemProps,
  ListBoxSection,
  type ListBoxSectionProps,
  Popover,
  type PopoverProps,
  SelectValue,
  type SelectValueProps,
  Separator,
  Text,
  type TextProps,
  type ValidationResult,
} from "react-aria-components";
import { ButtonProps, buttonStyles } from "./button";

/** Single mode reports the chosen key (or null); multiple mode reports every chosen key. */
export type SelectChange = Key | Key[] | null;

interface SelectContextValue {
  selectionMode?: "single" | "multiple";
  value?: Key | null | Iterable<Key>;
  onChange?: (value: SelectChange) => void;
  isRequired?: boolean;
  isInvalid?: boolean;
  name?: string;
}

const SelectContext = createContext<SelectContextValue | null>(null);

// Select (Root)

export interface SelectProps<T extends object> extends Omit<
  AriaSelectProps<T>,
  | "children"
  | "selectionMode"
  | "selectedKeys"
  | "defaultSelectedKeys"
  | "selectedKeys"
  | "defaultSelectedKeys"
  | "onSelectionChange"
  | "value"
  | "onChange"
> {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  items?: Iterable<T>;
  children?: React.ReactNode | ((item: T) => React.ReactNode);
  selectionMode?: "single" | "multiple";
  value?: Key | null | Iterable<Key>;
  onChange?: (value: SelectChange) => void;
}

/** The `displayName` of a compound child (SelectLabel, SelectTrigger, …), or undefined. */
function childDisplayName(child: React.ReactNode): string | undefined {
  if (!React.isValidElement(child) || typeof child.type === "string") return undefined;
  return (child.type as { displayName?: string }).displayName;
}

export function Select<T extends object>({ className, children, ...props }: SelectProps<T>) {
  if (props.selectionMode === "multiple") {
    const childrenArray = typeof children === "function" ? [] : React.Children.toArray(children);
    const label = childrenArray.find(
      (child) => childDisplayName(child) === "SelectLabel",
    );
    const description = childrenArray.find(
      (child) => childDisplayName(child) === "SelectDescription",
    );
    const trigger = childrenArray.find(
      (child) => childDisplayName(child) === "SelectTrigger",
    );
    const content = childrenArray.find(
      (child) => childDisplayName(child) === "SelectContent",
    );
    const errorMessage = childrenArray.find(
      (child) => childDisplayName(child) === "SelectErrorMessage",
    );

    const isValueEmpty =
      !props.value ||
      (Array.isArray(props.value) && props.value.length === 0) ||
      (props.value instanceof Set && props.value.size === 0) ||
      (typeof props.value === "object" &&
        Symbol.iterator in props.value &&
        Array.from(props.value as Iterable<Key>).length === 0);

    const isInvalid = props.isInvalid || (props.isRequired && isValueEmpty);

    return (
      <SelectContext.Provider value={{ ...props, isInvalid }}>
        <div
          className={cn("group inline-flex flex-col gap-2", className)}
          data-required={props.isRequired || undefined}
          data-invalid={isInvalid || undefined}
        >
          {label}
          {description}
          <DialogTrigger>
            {trigger}
            {content}
          </DialogTrigger>
          {errorMessage}
          {props.name && (
            <select
              name={props.name}
              multiple
              required={props.isRequired}
              value={Array.from((props.value as Iterable<Key>) || []) as string[]}
              onChange={() => {}}
              className="sr-only"
              aria-hidden="true"
              tabIndex={-1}
            >
              {Array.from((props.value as Iterable<Key>) || []).map((key) => (
                <option key={key.toString()} value={key.toString()}>
                  {key}
                </option>
              ))}
            </select>
          )}
        </div>
      </SelectContext.Provider>
    );
  }

  // Strip the compound-API props AriaSelect does not take; selection is wired explicitly below and this
  // branch is always single-selection (multiple returned above).
  const {
    label: _label,
    description: _description,
    errorMessage: _errorMessage,
    value: _value,
    onChange: _onChange,
    selectionMode: _selectionMode,
    ...ariaProps
  } = props;
  return (
    <AriaSelect
      {...ariaProps}
      className={cn("group flex w-full flex-col gap-2", className)}
      selectedKey={props.value as Key}
      onSelectionChange={props.onChange}
    >
      {/* The root takes compound children (SelectLabel, SelectTrigger, SelectContent); a render
          function belongs to SelectContent, never here. */}
      {typeof children === "function" ? null : children}
    </AriaSelect>
  );
}

// SelectTrigger

// React 19: `ref` is a prop; declared so callers (react-hook-form) can focus the trigger.
interface SelectTriggerProps extends AriaButtonProps, React.RefAttributes<HTMLButtonElement> {
  isInvalid?: boolean;
  variant?: ButtonProps["variant"];
  appearance?: ButtonProps["appearance"];
  iconOnly?: ButtonProps["iconOnly"];
  size?: ButtonProps["size"];
}

function SelectTrigger({
  className,
  appearance = "outline",
  children,
  ...props
}: SelectTriggerProps) {
  const context = useContext(SelectContext);
  const isInvalid = context?.isInvalid || props.isInvalid;

  return (
    <Button
      className={cn(
        buttonStyles({
          variant: props.variant,
          appearance: appearance,
          iconOnly: props.iconOnly,
          size: props.size,
        }),
        "justify-between border-card-border bg-button-primary-outline-background pr-2.5 pl-3 text-sm shadow-xs group-data-invalid:border-input-error-focus-border hover:bg-button-primary-outline-hover-background group-data-invalid:focus:ring-input-error-focus-border/20",
        className,
      )}
      {...(isInvalid !== undefined ? { "data-invalid": isInvalid || undefined } : {})}
      {...props}
    >
      {children}
    </Button>
  );
}
SelectTrigger.displayName = "SelectTrigger";

// SelectDisplayValue

function SelectDisplayValue<T extends object>({ className, ...props }: SelectValueProps<T>) {
  return (
    <SelectValue
      className={cn("truncate data-placeholder:text-input-placeholder-text", className)}
      {...props}
    />
  );
}

// Select Indicator

function SelectIndicator({ className, children, ...props }: ComponentProps<"span">) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "shrink-0 text-text-100 transition-transform",
        !children && "group-data-open:rotate-180",
        className,
      )}
      {...props}
    >
      {children ?? <ChevronDown className="size-4" />}
    </span>
  );
}

// Select Content

type SelectContentProps = PopoverProps;

function SelectContent({ children, className, ...props }: SelectContentProps) {
  const context = useContext(SelectContext);

  if (context) {
    // Multiple selection mode - render ListBox with context props
    return (
      <Popover
        className={cn(
          "w-(--trigger-width) overflow-auto rounded-lg bg-dropdowns-background shadow-md",
          "entering:animate-in entering:fade-in-0 entering:zoom-in-95",
          "exiting:animate-out exiting:fade-out-0 exiting:zoom-out-95",
          className,
        )}
        {...props}
      >
        <ListBox
          className="bg-background-white-secondary p-1.5 outline-none"
          selectionMode={context.selectionMode}
          selectedKeys={
            context.value instanceof Set
              ? context.value
              : new Set((context.value as Iterable<Key>) || [])
          }
          onSelectionChange={(keys) => {
            if (context.onChange) {
              context.onChange(Array.from(keys));
            }
          }}
        >
          {children}
        </ListBox>
      </Popover>
    );
  }

  // Single selection mode (standard Select)
  return (
    <Popover
      className={cn(
        "w-(--trigger-width) overflow-auto rounded-lg border border-card-border bg-background-white-secondary",
        "entering:animate-in entering:fade-in-0 entering:zoom-in-95",
        "exiting:animate-out exiting:fade-out-0 exiting:zoom-out-95",
        className,
      )}
      {...props}
    >
      <ListBox className="p-1.5 outline-none">{children}</ListBox>
    </Popover>
  );
}
SelectContent.displayName = "SelectContent";

// Select Item

function SelectItem({ className, children, ...props }: ListBoxItemProps) {
  return (
    <ListBoxItem
      className={cn(
        "group/item relative flex w-full cursor-pointer items-center gap-3 rounded-md py-1 pr-7 pl-1.5 text-sm text-text-secondary outline-hidden select-none focus:bg-background-gray-secondary_alt focus:text-text-primary",
        "data-disabled:pointer-events-none data-disabled:text-input-disabled-text",
        className,
      )}
      {...props}
    >
      {(renderProps) => (
        <>
          {typeof children === "function" ? children(renderProps) : children}
          {renderProps.isSelected && (
            <span className="absolute right-1 flex size-5 items-center justify-center">
              <Check className="size-5" />
            </span>
          )}
        </>
      )}
    </ListBoxItem>
  );
}

// Select Section

function SelectSection<T extends object>({ className, ...props }: ListBoxSectionProps<T>) {
  return <ListBoxSection {...props} className={cn("", className)} />;
}

// Select Header

function SelectHeader({ className, ...props }: ComponentProps<typeof Header>) {
  return (
    <Header
      {...props}
      className={cn("px-3 py-2 text-sm font-medium text-text-100 select-none", className)}
    />
  );
}

// Select Separator

function SelectSeparator({ className, ...props }: ComponentProps<"hr">) {
  return (
    <Separator
      className={cn("mx-1 my-1 h-px border-none bg-(--border-color-base-100)", className)}
      {...props}
    />
  );
}

// Select Label

function SelectLabel({ className, ...props }: LabelProps) {
  return (
    <Label
      className={cn("max-w-fit text-sm font-medium text-input-label-text select-none", className)}
      {...props}
    />
  );
}
SelectLabel.displayName = "SelectLabel";

// Select Description

function SelectDescription({ className, ...props }: TextProps) {
  return (
    <Text
      slot="description"
      className={cn("text-sm font-normal text-text-50", className)}
      {...props}
    />
  );
}
SelectDescription.displayName = "SelectDescription";

// Select Error Message

function SelectErrorMessage({ className, ...props }: TextProps) {
  const context = useContext(SelectContext);

  if (context?.selectionMode === "multiple") {
    if (!context.isInvalid) {
      return null;
    }
    return <Text {...props} className={cn("text-sm font-normal text-input-error", className)} />;
  }

  return (
    <FieldError className={cn("text-sm font-normal text-input-error", className)} {...props} />
  );
}
SelectErrorMessage.displayName = "SelectErrorMessage";

export {
  SelectContent,
  SelectDescription,
  SelectErrorMessage,
  SelectHeader,
  SelectIndicator,
  SelectItem,
  SelectLabel,
  SelectSection,
  SelectSeparator,
  SelectTrigger,
  SelectDisplayValue as SelectValue,
};
